import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  Container, 
  Grid, 
  Paper, 
  Typography, 
  Box, 
  Button, 
  Alert, 
  CircularProgress,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Stack,
  Card,
  CardContent,
  Chip,
  LinearProgress,
  Tooltip,
  IconButton,
  InputAdornment,
  Breadcrumbs,
  Link,
  Menu,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination
} from '@mui/material';
import HomeIcon from '@mui/icons-material/Home';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import FilterListIcon from '@mui/icons-material/FilterList';
import InsertChartIcon from '@mui/icons-material/InsertChart';
import RefreshIcon from '@mui/icons-material/Refresh';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import CloseIcon from '@mui/icons-material/Close';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import TableViewIcon from '@mui/icons-material/TableView';
import Layout from './Layout';
import { useNavigate } from 'react-router-dom';
import { getEndpointUrl } from '../config/api';

const Dashboard = () => {
  const [arquivos, setArquivos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState('todos');
  const [periodoFilter, setPeriodoFilter] = useState('todos');
  const [seguradoraFilter, setSeguradoraFilter] = useState('todos');
  const [dataImportacaoFilter, setDataImportacaoFilter] = useState('');
  const [dataInicialFilter, setDataInicialFilter] = useState('');
  const [dataFinalFilter, setDataFinalFilter] = useState('');
  const [sortOrder, setSortOrder] = useState('desc');
  const [searchTerm, setSearchTerm] = useState('');
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [selectedFileId, setSelectedFileId] = useState(null);
  
  // Estados para o modal de upload
  const [openUploadModal, setOpenUploadModal] = useState(false);
  const [file, setFile] = useState(null);
  const [dataInicial, setDataInicial] = useState('');
  const [dataFinal, setDataFinal] = useState('');
  const [seguradoraSelecionada, setSeguradoraSelecionada] = useState('');
  const [uploadLoading, setUploadLoading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [conflitosTroca, setConflitosTroca] = useState([]);
  const [previewData, setPreviewData] = useState(null);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [totalArquivos, setTotalArquivos] = useState(0);
  const [confirmUpload, setConfirmUpload] = useState(false);
  const [dadosTemporarios, setDadosTemporarios] = useState(null);
  const [totalRegistros, setTotalRegistros] = useState(0);
  
  // Estatísticas dos arquivos
  const [stats, setStats] = useState({
    total: 0,
    processados: 0,
    pendentes: 0,
    comErro: 0
  });

  // Estados para o diálogo de confirmação de exclusão
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [deleteTargetId, setDeleteTargetId] = useState(null);

  // Estado para diálogo de registros não processados
  const [openNaoProcessadosDialog, setOpenNaoProcessadosDialog] = useState(false);
  const [registrosNaoProcessados, setRegistrosNaoProcessados] = useState(0);
  const [corretorasSemComercial, setCorretorasSemComercial] = useState([]);
  const [importacaoId, setImportacaoId] = useState(null);
  
  // Hook do React Router para navegação
  const navigate = useNavigate();

  // Lista de seguradoras suportadas, em ordem alfabética
  const SEGURADORAS = [
    'DARWIN',
    'DEMAIS_SEGURADORAS',
    'EZZE',
    'HDI',
    'ITURAN',
    'JUSTOS',
    'SUHAI',
    'TOKIO',
    'YELUM',
  ];

  // Util: parse número em formato BR/variações
  const parseNumeroBr = (valor) => {
    if (valor == null) return 0;
    if (typeof valor === 'number') return isFinite(valor) ? valor : 0;
    
    let s = String(valor).trim();
    if (!s) return 0;
    
    // Remove símbolos monetários e espaços
    s = s.replace(/[R$\s\u00A0]/g, '');
    
    // Se não tem separadores, é um número simples
    if (!/[,.]/.test(s)) {
      const n = parseFloat(s);
      return isNaN(n) || !isFinite(n) ? 0 : n;
    }
    
    // Padrões robustos baseados nas melhores práticas internacionais
    
    // Padrão brasileiro: 1.234.567,89 (pontos para milhares, vírgula para decimal)
    if (/^\d{1,3}(\.\d{3})*,\d{1,2}$/.test(s)) {
      return parseFloat(s.replace(/\./g, '').replace(',', '.'));
    }
    
    // Padrão americano: 1,234,567.89 (vírgulas para milhares, ponto para decimal)
    if (/^\d{1,3}(,\d{3})*\.\d{1,2}$/.test(s)) {
      return parseFloat(s.replace(/,/g, ''));
    }
    
    // Apenas vírgula decimal (sem separador de milhares): 1234,89
    if (/^\d+,\d{1,2}$/.test(s)) {
      return parseFloat(s.replace(',', '.'));
    }
    
    // Apenas ponto decimal (sem separador de milhares): 1234.89
    if (/^\d+\.\d{1,2}$/.test(s)) {
      return parseFloat(s);
    }
    
    // Fallback: detectar pelo contexto (último separador é decimal)
    const lastComma = s.lastIndexOf(',');
    const lastDot = s.lastIndexOf('.');
    
    if (lastComma > lastDot) {
      // Vírgula é decimal, pontos são milhares
      const parts = s.split(',');
      if (parts.length === 2 && parts[1].length <= 2) {
        return parseFloat(s.replace(/\./g, '').replace(',', '.'));
      }
    } else if (lastDot > lastComma) {
      // Ponto é decimal, vírgulas são milhares
      const parts = s.split('.');
      if (parts.length >= 2 && parts[parts.length - 1].length <= 2) {
        return parseFloat(s.replace(/,/g, ''));
      }
    }
    
    // Se nada funcionou, tenta conversão direta removendo tudo exceto último separador
    const cleanNumber = s.replace(/[^\d,.]/g, '');
    if (lastComma > lastDot) {
      return parseFloat(cleanNumber.replace(/\./g, '').replace(',', '.')) || 0;
    } else {
      return parseFloat(cleanNumber.replace(/,/g, '')) || 0;
    }
  };

  // Mapeia colunas de prêmio por seguradora para soma
  const colunasPremioPorSeguradora = (seguradora) => {
    const s = (seguradora || '').toUpperCase();
    // Ordem importa; a primeira encontrada na linha será usada
    const map = {
      DARWIN: [
        'Total Prêmio Carteira',
        'Total Premio Carteira',
        'Total Premio',
        'Total',
        'Valor',
      ],
      // Para HDI usar EXCLUSIVAMENTE a coluna abaixo (sem variações)
      HDI: ['Prêmio Líquido Emitido'],
      EZZE: ['Premio', 'Prêmio', 'Valor', 'Total', 'VALOR TOTAL', 'Valor Total'],
      SUHAI: ['Premio', 'Prêmio', 'Valor', 'Total', 'VALOR TOTAL', 'Valor Total'],
      // ITURAN costuma vir com coluna 'VL_TOTAL'
      ITURAN: ['VL_TOTAL', 'VL TOTAL', 'VL-TOTAL', 'Premio', 'Prêmio', 'Valor', 'Total', 'VALOR TOTAL', 'Valor Total'],
      JUSTOS: ['Total Prêmio Carteira', 'Total Premio Carteira', 'Premio', 'Prêmio', 'Valor', 'Total'],
      // TOKIO pode aparecer como 'Prêmio Líquido'
      TOKIO: ['Prêmio Líquido', 'Premio Liquido', 'Premio', 'Prêmio', 'Valor', 'Total'],
    };
    return map[s] || ['Premio', 'Prêmio', 'Valor', 'Total'];
  };

  const calcularTotalPremios = () => {
    const linhas = dadosTemporarios?.dados_processados;
    if (!Array.isArray(linhas) || linhas.length === 0) return 0;
    const candidatos = colunasPremioPorSeguradora(dadosTemporarios?.seguradora);
    let total = 0;
    for (const row of linhas) {
      if (row && typeof row === 'object') {
        let somado = false;
        for (const key of candidatos) {
          if (Object.prototype.hasOwnProperty.call(row, key)) {
            total += parseNumeroBr(row[key]);
            somado = true;
            break;
          }
        }
        if (!somado) {
          // Para HDI, não aplicar fallback para evitar divergência
          if ((dadosTemporarios?.seguradora || '').toUpperCase() !== 'HDI') {
            const chaves = Object.keys(row);
            const chavePremio = chaves.find((k) => k && typeof k === 'string' && /pr[eê]mio|premio/i.test(k));
            if (chavePremio) {
              total += parseNumeroBr(row[chavePremio]);
            } else {
              const chaveValor = chaves.find((k) => k && typeof k === 'string' && /valor/i.test(k));
              if (chaveValor) total += parseNumeroBr(row[chaveValor]);
            }
          }
        }
      }
    }
    return total;
  };

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      window.location.href = '/login';
    }
  }, []);

  // Refetch quando pagina/tamanho/ordenacao mudam
  useEffect(() => {
    fetchArquivos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize, sortOrder]);

  // Refetch (debounced) quando filtros mudam - reseta pagina
  useEffect(() => {
    const t = setTimeout(() => {
      if (page !== 0) {
        setPage(0);
      } else {
        fetchArquivos();
      }
    }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, seguradoraFilter, periodoFilter, dataImportacaoFilter, dataInicialFilter, dataFinalFilter]);

  // Função para abrir o modal de upload
  const handleOpenUploadDialog = () => {
    setOpenUploadModal(true);
    setFile(null);
    setDataInicial('');
    setDataFinal('');
    setUploadError(null);
    setConflitosTroca([]);
    setPreviewData(null);
    setConfirmUpload(false);
    setDadosTemporarios(null);
    setTotalRegistros(0);
  };

  // Função para fechar o modal de upload
  const handleCloseUploadDialog = () => {
    setOpenUploadModal(false);
    // Limpar todos os estados do modal
    setFile(null);
    setDataInicial('');
    setDataFinal('');
    setSeguradoraSelecionada('');
    setUploadError(null);
    setConflitosTroca([]);
    setConfirmUpload(false);
    setDadosTemporarios(null);
    setTotalRegistros(0);
    setPreviewData(null);
  };

  // Função para lidar com a mudança de arquivo
  const handleFileChange = (event) => {
    const selectedFile = event.target.files[0];
    if (selectedFile) {
      setFile(selectedFile);
      setUploadError(null);
      setConflitosTroca([]);
    }
  };

  // Função para processar o upload do arquivo
  const handleProcessarArquivo = async () => {
    if (!file) {
      setUploadError('Por favor, selecione um arquivo para upload.');
      return;
    }

    if (!seguradoraSelecionada) {
      setUploadError('Por favor, selecione uma seguradora.');
      return;
    }

    // Para outras seguradoras (não DEMAIS_SEGURADORAS), as datas são obrigatórias
    if (seguradoraSelecionada !== 'DEMAIS_SEGURADORAS' && (!dataInicial || !dataFinal)) {
      setUploadError('Por favor, selecione as datas inicial e final.');
      return;
    }

    try {
      setUploadLoading(true);
      setUploadError(null);
      setConflitosTroca([]);
      
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', file);
      formData.append('seguradora', seguradoraSelecionada);
      
      // Para DEMAIS_SEGURADORAS não enviar datas
      if (seguradoraSelecionada !== 'DEMAIS_SEGURADORAS') {
      formData.append('data_inicial', dataInicial);
      formData.append('data_final', dataFinal);
      }
      
      console.log('Enviando dados para API:', {
        arquivo: file.name,
        tamanho: file.size,
        tipo: file.type,
        seguradora: seguradoraSelecionada,
        data_inicial: seguradoraSelecionada !== 'DEMAIS_SEGURADORAS' ? dataInicial : 'N/A',
        data_final: seguradoraSelecionada !== 'DEMAIS_SEGURADORAS' ? dataFinal : 'N/A'
      });

      console.log('Enviando requisição para API...');
      const response = await axios({
        method: 'post',
        url: `${process.env.REACT_APP_API_URL || '/api'}/importacao`,
        data: formData,
        headers: {
          'Content-Type': 'multipart/form-data',
          'Authorization': `Bearer ${token}`
        }
      });
      
      console.log('Resposta completa recebida', response);
      
      // Simplificar o processamento para reduzir pontos de falha
      const data = response.data;
      
      // Verificar se temos preview e dados_temporarios
      if (data && data.preview && Array.isArray(data.preview) && data.preview.length > 0) {
        console.log('Utilizando dados de preview da resposta');
        setPreviewData(data.preview);
        
        // Verificar ou construir dados temporários
        if (data.dados_temporarios) {
          console.log('Utilizando dados_temporarios da resposta');
          // Para DEMAIS_SEGURADORAS, incluir as estatísticas adicionais
          const dadosComEstatisticas = {
            ...data.dados_temporarios,
            // Garantir que a seguradora esteja definida
            seguradora: seguradoraSelecionada,
            nome_arquivo: file.name,
            // Adicionar dados específicos para DEMAIS_SEGURADORAS
            ...(seguradoraSelecionada === 'DEMAIS_SEGURADORAS' && {
              total_registros: data.total_registros,
              total_premio_geral: data.total_premio_geral,
              total_seguradoras: data.total_seguradoras,
              stats_por_seguradora: data.stats_por_seguradora,
              seguradoras_encontradas: data.seguradoras_encontradas
            })
          };
          setDadosTemporarios(dadosComEstatisticas);
        } else {
          // Criar dados temporários manualmente se não existirem
          console.log('Criando dados_temporarios manualmente');
          const dadosTemp = {
            nome_arquivo: file.name,
            data_inicial: seguradoraSelecionada !== 'DEMAIS_SEGURADORAS' ? dataInicial : undefined,
            data_final: seguradoraSelecionada !== 'DEMAIS_SEGURADORAS' ? dataFinal : undefined,
            seguradora: seguradoraSelecionada,
            dados_processados: data.preview,
            // Para DEMAIS_SEGURADORAS, incluir estatísticas se disponíveis
            ...(seguradoraSelecionada === 'DEMAIS_SEGURADORAS' && {
              total_registros: data.total_registros,
              total_premio_geral: data.total_premio_geral,
              total_seguradoras: data.total_seguradoras,
              stats_por_seguradora: data.stats_por_seguradora,
              seguradoras_encontradas: data.seguradoras_encontradas
            })
          };
          setDadosTemporarios(dadosTemp);
        }
        
        // Definir total de registros
        setTotalRegistros(data.total_registros || data.preview.length);
        
        // Ativar confirmação
        setConfirmUpload(true);
      } else {
        console.error('Resposta sem dados de preview válidos:', data);
        throw new Error('Resposta da API não contém dados válidos de preview');
      }
      
    } catch (err) {
      console.error('Erro ao processar arquivo:', err);
      if (err.response) {
        console.error('Detalhes da resposta de erro:', {
          status: err.response.status,
          statusText: err.response.statusText,
          data: err.response.data
        });
        setUploadError(`Erro ${err.response.status}: ${err.response.data?.message || 'Erro ao processar o arquivo'}`);
      } else if (err.request) {
        console.error('Nenhuma resposta recebida:', err.request);
        setUploadError('Não foi possível se comunicar com o servidor. Verifique sua conexão.');
      } else {
        setUploadError(err.message || 'Erro ao processar o arquivo. Por favor, tente novamente.');
      }
    } finally {
      setUploadLoading(false);
    }
  };

  // Função para confirmar a importação
  const handleDownloadConflitosExcel = async () => {
    if (!conflitosTroca.length) return;
    try {
      const XLSX = await import('xlsx');
      const linhas = conflitosTroca.map(c => ({
        'Corretora / Empresa': c.empresa,
        'Seguradora': c.seguradora,
        'Responsavel anterior': c.responsavel_antigo,
        'Novo responsavel': c.responsavel_novo,
        'Data da troca': c.data_troca,
        'Arquivo 1 (sugestao)': c.sugestao?.arquivo_1 || '',
        'Arquivo 2 (sugestao)': c.sugestao?.arquivo_2 || '',
      }));
      const ws = XLSX.utils.json_to_sheet(linhas);
      ws['!cols'] = [
        { wch: 45 }, { wch: 20 }, { wch: 22 }, { wch: 22 },
        { wch: 14 }, { wch: 55 }, { wch: 55 },
      ];
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Trocas de comercial');
      const ts = new Date().toISOString().replace(/[:T]/g, '-').slice(0, 19);
      XLSX.writeFile(wb, `trocas_comercial_${ts}.xlsx`);
    } catch (e) {
      console.error('Erro ao gerar Excel de conflitos:', e);
    }
  };

  const handleConfirmarImportacao = async () => {
    try {
      setUploadLoading(true);
      setUploadError(null);
      setConflitosTroca([]);

      // Log de depuração para verificar os dados sendo enviados
      console.log('Enviando para confirmação - dados temporários:', dadosTemporarios);
      
      if (!dadosTemporarios) {
        throw new Error('Dados temporários não disponíveis para importação');
      }
      
      // Se não temos todos os campos obrigatórios, mas temos dados processados, podemos tentar recuperar
      if ((!dadosTemporarios.nome_arquivo || !dadosTemporarios.data_inicial || 
          !dadosTemporarios.data_final || !dadosTemporarios.seguradora) && 
          dadosTemporarios.dados_processados) {
        
        console.log('Dados temporários incompletos, tentando reconstruir...');
        
        // Usar o nome do arquivo atual se não estiver definido
        if (!dadosTemporarios.nome_arquivo && file) {
          dadosTemporarios.nome_arquivo = file.name;
          console.log('Nome do arquivo recuperado do objeto file:', file.name);
        }
        
        // Usar as datas do formulário se não estiverem definidas
        if (!dadosTemporarios.data_inicial && dataInicial) {
          dadosTemporarios.data_inicial = dataInicial;
          console.log('Data inicial recuperada do formulário:', dataInicial);
        }
        
        if (!dadosTemporarios.data_final && dataFinal) {
          dadosTemporarios.data_final = dataFinal;
          console.log('Data final recuperada do formulário:', dataFinal);
        }
        
        // Usar a seguradora selecionada pelo usuário
        if (!dadosTemporarios.seguradora && seguradoraSelecionada) {
          dadosTemporarios.seguradora = seguradoraSelecionada;
          console.log('Seguradora definida pela seleção do usuário:', seguradoraSelecionada);
        }
      }
      
      // Verificação final dos campos necessários
      const camposObrigatorios = ['nome_arquivo', 'seguradora', 'dados_processados'];
      
      // Para outras seguradoras (não DEMAIS_SEGURADORAS), datas são obrigatórias
      if (dadosTemporarios.seguradora !== 'DEMAIS_SEGURADORAS') {
        camposObrigatorios.push('data_inicial', 'data_final');
      }
      
      // Inicializar dados para validação
      let dadosParaValidacao = dadosTemporarios;
      
      // Verificar se dados_processados existe e é um array
      if (!dadosTemporarios.dados_processados) {
        // Se dadosTemporarios é um objeto com índices numéricos, convertê-lo para array
        if (typeof dadosTemporarios === 'object' && dadosTemporarios !== null) {
          const keys = Object.keys(dadosTemporarios);
          const numericKeys = keys.filter(key => !isNaN(key)).sort((a, b) => Number(a) - Number(b));
          
          if (numericKeys.length > 0) {
            // Converter objeto com índices numéricos para array
            const dadosArray = numericKeys.map(key => dadosTemporarios[key]);
            
            // Reestruturar dadosTemporarios corretamente
            const dadosReestruturados = {
              nome_arquivo: file.name,
              seguradora: seguradoraSelecionada,
              dados_processados: dadosArray,
              // Para DEMAIS_SEGURADORAS, incluir estatísticas se disponíveis
              ...(seguradoraSelecionada === 'DEMAIS_SEGURADORAS' && {
                total_registros: dadosTemporarios.total_registros,
                total_premio_geral: dadosTemporarios.total_premio_geral,
                total_seguradoras: dadosTemporarios.total_seguradoras,
                stats_por_seguradora: dadosTemporarios.stats_por_seguradora,
                seguradoras_encontradas: dadosTemporarios.seguradoras_encontradas
              }),
              // Para outras seguradoras, incluir datas se necessário
              ...(seguradoraSelecionada !== 'DEMAIS_SEGURADORAS' && {
                data_inicial: dataInicial,
                data_final: dataFinal
              })
            };
            
            // Atualizar o estado corretamente
            setDadosTemporarios(dadosReestruturados);
            
            console.log('Dados reestruturados:', dadosReestruturados);
            
            // Usar dados reestruturados diretamente para validação e envio
            dadosParaValidacao = dadosReestruturados;
          }
        }
      }
      
      const camposFaltantes = camposObrigatorios.filter(campo => !dadosParaValidacao[campo]);
      
      if (camposFaltantes.length > 0) {
        console.error('Dados temporários incompletos:', camposFaltantes, dadosParaValidacao);
        throw new Error(`Dados temporários incompletos para importação: ${camposFaltantes.join(', ')}`);
      }
      
      const token = localStorage.getItem('token');
      
      console.log('Enviando solicitação de confirmação para a API com dados recuperados:', dadosParaValidacao);
      const response = await axios({
        method: 'post',
                  url: `${process.env.REACT_APP_API_URL || '/api'}/importacao/confirmar`,
        data: dadosParaValidacao,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });
      
      console.log('Resposta da confirmação:', response.data);
      
      // O backend responde no formato { success, message, data: { ... } }
      const resultado = response.data?.data || response.data || {};
      const qtdNaoProcessados = Number(resultado.registros_nao_processados) || 0;
      const semComercial = Array.isArray(resultado.corretoras_sem_comercial) ? resultado.corretoras_sem_comercial : [];

      setOpenUploadModal(false);
      if (qtdNaoProcessados > 0 || semComercial.length > 0) {
        setRegistrosNaoProcessados(qtdNaoProcessados);
        setCorretorasSemComercial(semComercial);
        setImportacaoId(resultado.id_importacao ?? resultado.importacao_id ?? null);
        setOpenNaoProcessadosDialog(true);
      }
      
      fetchArquivos(); // Recarregar a lista após confirmar importação
      
    } catch (err) {
      console.error('Erro ao confirmar importação:', err);
      
      if (err.response) {
        console.error('Detalhes da resposta de erro:', {
          status: err.response.status,
          statusText: err.response.statusText,
          data: err.response.data
        });
        const detalhes = err.response.data?.details;
        if (detalhes?.codigo === 'TROCA_COMERCIAL_NO_PERIODO' && Array.isArray(detalhes.conflitos)) {
          setConflitosTroca(detalhes.conflitos);
          setUploadError(err.response.data?.message || 'Não é possível importar: houve troca de comercial no período do arquivo.');
        } else {
          setUploadError(`Erro ${err.response.status}: ${err.response.data?.message || 'Erro ao confirmar a importação'}`);
        }
      } else if (err.request) {
        console.error('Nenhuma resposta recebida:', err.request);
        setUploadError('Não foi possível se comunicar com o servidor. Verifique sua conexão.');
      } else {
        setUploadError(err.message || 'Erro ao confirmar a importação. Por favor, tente novamente.');
      }
    } finally {
      setUploadLoading(false);
    }
  };

  // Função para cancelar a importação
  const handleCancelarImportacao = async () => {
    try {
      const token = localStorage.getItem('token');
      
      await axios({
        method: 'post',
                  url: `${process.env.REACT_APP_API_URL || '/api'}/importacao/cancelar`,
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      setConfirmUpload(false);
      setPreviewData(null);
      setDadosTemporarios(null);
      
    } catch (err) {
      console.error('Erro ao cancelar importação:', err);
    }
  };

  // Função para buscar os arquivos da API (server-side paginado + filtrado)
  const fetchArquivos = async () => {
    try {
      setLoading(true);
      setError(null);
      const token = localStorage.getItem('token');

      if (!token) {
        window.location.href = '/login';
        return;
      }

      const params = {
        page: page + 1,
        page_size: pageSize,
        sort: sortOrder,
      };
      if (searchTerm) params.search = searchTerm;
      if (seguradoraFilter && seguradoraFilter !== 'todos') params.seguradora = seguradoraFilter;
      if (periodoFilter && periodoFilter !== 'todos') params.periodo = periodoFilter;
      if (dataImportacaoFilter) params.data_importacao = dataImportacaoFilter;
      if (dataInicialFilter) params.data_inicial = dataInicialFilter;
      if (dataFinalFilter) params.data_final = dataFinalFilter;

      const response = await axios({
        method: 'get',
        url: `${process.env.REACT_APP_API_URL || '/api'}/importacoes`,
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        withCredentials: false,
        params,
      });

      const payload = response.data || {};
      const arquivosData = Array.isArray(payload.arquivos)
        ? payload.arquivos
        : (Array.isArray(payload) ? payload : []);
      const total = Number.isFinite(payload.total) ? payload.total : arquivosData.length;

      const dadosProcessados = arquivosData.map(a => ({
        ...a,
        id: a.id || a.Id,
        nome: a.nome_arquivo || a['Nome Do arquivo'] || 'Arquivo sem nome',
        seguradora: a.seguradora || a.Seguradora || 'N/A',
        usuario: a.usuario || a.Usuario || a.username || 'N/A',
        data_upload: a.data_importacao || a['Data de Importação'] || new Date().toISOString(),
        data_inicial: a.data_inicial || a['Data Inicial'] || null,
        data_final: a.data_final || a['Data Final'] || null,
        status: 'processado',
      }));

      setArquivos(dadosProcessados);
      setTotalArquivos(total);
      setStats({
        total,
        processados: total,
        pendentes: 0,
        comErro: 0,
      });

      setLoading(false);
    } catch (err) {
      console.error('Erro ao buscar arquivos:', err);
      setError('Erro ao carregar os arquivos. Por favor, tente novamente.');
      setLoading(false);
    }
  };

  // Função para limpar os filtros
  const handleLimparFiltros = () => {
    setStatusFilter('todos');
    setPeriodoFilter('todos');
    setSeguradoraFilter('todos');
    setDataImportacaoFilter('');
    setDataInicialFilter('');
    setDataFinalFilter('');
    setSearchTerm('');
  };

  // Função para filtrar os arquivos
  // Paginacao e filtros sao server-side; a lista ja vem pronta.
  const sortedArquivos = arquivos;
  
  // Formatação de data para exibição
  const formatarData = (dataString) => {
    if (!dataString) return 'N/A';
    
    // Se a data estiver no formato YYYY-MM-DD, converter para DD/MM/YYYY
    if (dataString.match(/^\d{4}-\d{2}-\d{2}$/)) {
      const [year, month, day] = dataString.split('-');
      return `${day}/${month}/${year}`;
    }
    
    // Se a data já estiver no formato DD/MM/YYYY, retornar como está
    if (dataString.match(/^\d{2}\/\d{2}\/\d{4}$/)) {
      return dataString;
    }
    
    // Para outros formatos, tentar converter para formato brasileiro
    try {
      // Evitar criar objetos Date diretamente quando possível
      const parts = dataString.split(/[-T:]/);
      if (parts.length >= 3) {
        const year = parts[0];
        const month = parts[1];
        const day = parts[2].substring(0, 2);
        
        if (parts.length >= 5) {
          // Se tiver informação de hora
          const hour = parts[3];
          const minute = parts[4];
          return `${day}/${month}/${year} ${hour}:${minute}`;
        } else {
          // Apenas data
          return `${day}/${month}/${year}`;
        }
      }
    } catch (error) {
      console.error("Erro ao formatar data:", error);
    }
    
    // Fallback - usar com cuidado, pois pode causar problemas de fuso horário
    try {
      const data = new Date(dataString);
      if (isNaN(data.getTime())) return dataString; // Retornar original se inválida
      
      return data.toLocaleDateString('pt-BR', {
        day: '2-digit', 
        month: '2-digit', 
        year: 'numeric'
      });
    } catch (error) {
      return dataString; // Em caso de erro, retornar o valor original
    }
  };
  
  // Formatação apenas da data
  const formatarDataSimples = (dataString) => {
    if (!dataString) return 'N/A';
    
    // Se a data estiver no formato YYYY-MM-DD, converter para DD/MM/YYYY
    if (dataString.match(/^\d{4}-\d{2}-\d{2}$/)) {
      const [year, month, day] = dataString.split('-');
      return `${day}/${month}/${year}`;
    }
    
    // Se a data já estiver no formato DD/MM/YYYY, retornar como está
    if (dataString.match(/^\d{2}\/\d{2}\/\d{4}$/)) {
      return dataString;
    }
    
    // Para outros formatos, tentar converter manualmente
    try {
      const parts = dataString.split(/[-T]/);
      if (parts.length >= 3) {
        const year = parts[0];
        const month = parts[1];
        const day = parts[2].substring(0, 2);
        return `${day}/${month}/${year}`;
      }
    } catch (error) {
      console.error("Erro ao formatar data simples:", error);
    }
    
    // Fallback - usar só se necessário
    try {
      const data = new Date(dataString);
      if (isNaN(data.getTime())) return dataString; // Retornar original se inválida
      
      return data.toLocaleDateString('pt-BR', {
        day: '2-digit', 
        month: '2-digit', 
        year: 'numeric'
      });
    } catch (error) {
      return dataString; // Em caso de erro, retornar o valor original
    }
  };
  
  // Cor baseada no status
  const getStatusColor = (status) => {
    switch (status) {
      case 'processado':
        return 'success';
      case 'pendente':
        return 'warning';
      case 'erro':
        return 'error';
      default:
        return 'default';
    }
  };
  
  // Função para traduzir o status
  const traduzirStatus = (status) => {
    switch (status) {
      case 'processado':
        return 'Processado';
      case 'pendente':
        return 'Pendente';
      case 'erro':
        return 'Erro';
      default:
        return status;
    }
  };
  
  // Handler para abertura do menu de ações
  const handleMenuOpen = (event, id) => {
    setMenuAnchorEl(event.currentTarget);
    setSelectedFileId(id);
  };
  
  // Handler para fechamento do menu de ações
  const handleMenuClose = () => {
    setMenuAnchorEl(null);
    setSelectedFileId(null);
  };
  
  // Handler para visualizar registros não processados de uma importação
  const handleVerRegistrosNaoProcessadosMenu = (id) => {
    navigate(`/registros-nao-processados/${id}`);
    handleMenuClose();
  };
  
  // Handler para exportar registros não processados de uma importação para Excel
  const handleExportarRegistrosNaoProcessadosMenu = (id) => {
    const token = localStorage.getItem('token');
    const url = getEndpointUrl(`/importacao/${id}/exportar-nao-processados`);
    const sep = url.includes('?') ? '&' : '?';
    window.open(`${url}${sep}token=${encodeURIComponent(token || '')}`, '_blank');
    handleMenuClose();
  };
  
  // Função para abrir o diálogo de confirmação de exclusão
  const handleOpenDeleteDialog = (id) => {
    setDeleteTargetId(id);
    setOpenDeleteDialog(true);
    handleMenuClose();
  };

  // Função para fechar o diálogo de confirmação de exclusão
  const handleCloseDeleteDialog = () => {
    setOpenDeleteDialog(false);
    setDeleteTargetId(null);
  };

  // Função para excluir uma importação
  const handleExcluirImportacao = async () => {
    if (!deleteTargetId) return;
    
    try {
      setLoading(true);
      setError(null); // Limpar erros anteriores
      const token = localStorage.getItem('token');
      
      await axios({
        method: 'delete',
                  url: `${process.env.REACT_APP_API_URL || '/api'}/importacoes/${deleteTargetId}`,
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      // Atualizar a lista de arquivos
      fetchArquivos();
      handleCloseDeleteDialog();
      
    } catch (err) {
      console.error('Erro ao excluir importação:', err);
      
      // Verificar se há uma mensagem detalhada do servidor
      if (err.response) {
        console.error('Detalhes da resposta de erro:', {
          status: err.response.status,
          statusText: err.response.statusText,
          data: err.response.data
        });
        
        // Verificar o código de status para fornecer mensagens adequadas
        if (err.response.status === 403) {
          // Acesso negado - usuário tentando excluir importação de outro usuário
          setError(err.response.data?.message || 'Você não tem permissão para excluir esta importação.');
        } else if (err.response.status === 404) {
          // Importação não encontrada
          setError(err.response.data?.message || 'Importação não encontrada.');
        } else {
          // Outros erros
          setError(err.response.data?.message || 'Erro ao excluir importação. Por favor, tente novamente.');
        }
      } else if (err.request) {
        // A requisição foi feita mas não houve resposta
        setError('Não foi possível se comunicar com o servidor. Verifique sua conexão.');
      } else {
        // Erro na configuração da requisição
        setError('Erro ao excluir importação. Por favor, tente novamente.');
      }
      
      // Manter o diálogo aberto para mostrar o erro, exceto para o erro 404
      if (err.response && err.response.status === 404) {
        handleCloseDeleteDialog();
      }
      
    } finally {
      setLoading(false);
    }
  };

  // Função para visualizar os registros não processados
  const handleVerRegistrosNaoProcessados = () => {
    if (importacaoId) {
      navigate(`/registros-nao-processados/${importacaoId}`);
      setOpenNaoProcessadosDialog(false);
    }
  };
  
  // Função para exportar os registros não processados para Excel
  const handleExportarRegistrosNaoProcessados = () => {
    if (importacaoId) {
      const token = localStorage.getItem('token');
      const url = getEndpointUrl(`/importacao/${importacaoId}/exportar-nao-processados`);
      const sep = url.includes('?') ? '&' : '?';
      window.open(`${url}${sep}token=${encodeURIComponent(token || '')}`, '_blank');
    }
  };

  return (
    <Layout>
      <Container maxWidth="lg">
        {/* Cabeçalho e navegação */}
        <Box sx={{ mb: 4 }}>
          <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 2 }}>
            <Link 
              underline="hover" 
              color="inherit" 
              href="/importacao"
              sx={{ display: 'flex', alignItems: 'center' }}
            >
              <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
              Início
            </Link>
            <Typography color="text.primary">Importação de Arquivos</Typography>
          </Breadcrumbs>
          
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
            <Box>
              <Typography variant="h5" gutterBottom>
                Importação de Arquivos
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Gerencie suas importações de arquivos e acompanhe o status de processamento
              </Typography>
            </Box>
            <Button
              variant="contained"
              color="success"
              onClick={handleOpenUploadDialog}
              startIcon={<CloudUploadIcon />}
            >
              Importar Novo Arquivo
            </Button>
          </Stack>
        </Box>
        
        {/* Cards de estatísticas */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card 
              sx={{ 
                height: '100%', 
                display: 'flex', 
                flexDirection: 'column',
                borderLeft: '5px solid',
                borderColor: 'primary.main',
              }}
            >
              <CardContent>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Total de Arquivos
                </Typography>
                <Typography variant="h3" component="div" color="text.primary" sx={{ mb: 1 }}>
                  {loading ? <CircularProgress size={30} /> : stats.total}
                </Typography>
                <Box sx={{ mt: 1, display: 'flex', alignItems: 'center' }}>
                  <FileUploadIcon color="primary" fontSize="small" sx={{ mr: 1 }} />
                  <Typography variant="body2" color="text.secondary">
                    Todos os arquivos
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <Card 
              sx={{ 
                height: '100%', 
                display: 'flex', 
                flexDirection: 'column',
                borderLeft: '5px solid',
                borderColor: 'success.main',
              }}
            >
              <CardContent>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Processados
                </Typography>
                <Typography variant="h3" component="div" color="success.main" sx={{ mb: 1 }}>
                  {loading ? <CircularProgress size={30} /> : stats.processados}
                </Typography>
                <Box sx={{ mt: 1, display: 'flex', alignItems: 'center' }}>
                  <Typography variant="body2" color="text.secondary">
                    {loading ? '' : `${Math.round((stats.processados / stats.total) * 100) || 0}% do total`}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <Card 
              sx={{ 
                height: '100%', 
                display: 'flex', 
                flexDirection: 'column',
                borderLeft: '5px solid',
                borderColor: 'warning.main',
              }}
            >
              <CardContent>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Pendentes
                </Typography>
                <Typography variant="h3" component="div" color="warning.main" sx={{ mb: 1 }}>
                  {loading ? <CircularProgress size={30} /> : stats.pendentes}
                </Typography>
                <Box sx={{ mt: 1, display: 'flex', alignItems: 'center' }}>
                  <Typography variant="body2" color="text.secondary">
                    {loading ? '' : `${Math.round((stats.pendentes / stats.total) * 100) || 0}% do total`}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <Card 
              sx={{ 
                height: '100%', 
                display: 'flex', 
                flexDirection: 'column',
                borderLeft: '5px solid',
                borderColor: 'error.main',
              }}
            >
              <CardContent>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Com Erro
                </Typography>
                <Typography variant="h3" component="div" color="error.main" sx={{ mb: 1 }}>
                  {loading ? <CircularProgress size={30} /> : stats.comErro}
                </Typography>
                <Box sx={{ mt: 1, display: 'flex', alignItems: 'center' }}>
                  <Typography variant="body2" color="text.secondary">
                    {loading ? '' : `${Math.round((stats.comErro / stats.total) * 100) || 0}% do total`}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
        
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}
        
        {/* Filtros */}
        <Paper elevation={0} variant="outlined" sx={{ p: 2, mb: 3 }}>
          <Typography variant="subtitle1" sx={{ mb: 2 }}>
            Filtros
          </Typography>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                variant="outlined"
                size="small"
                label="Buscar arquivos"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <FilterListIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            
            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel id="seguradora-filter-label">Seguradora</InputLabel>
                <Select
                  labelId="seguradora-filter-label"
                  value={seguradoraFilter}
                  label="Seguradora"
                  onChange={(e) => setSeguradoraFilter(e.target.value)}
                >
                  <MenuItem value="todos">Todas</MenuItem>
                  {SEGURADORAS.map((seg) => (
                    <MenuItem key={seg} value={seg}>{seg.charAt(0) + seg.slice(1).toLowerCase()}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel id="periodo-filter-label">Período Padrão</InputLabel>
                <Select
                  labelId="periodo-filter-label"
                  value={periodoFilter}
                  label="Período Padrão"
                  onChange={(e) => setPeriodoFilter(e.target.value)}
                >
                  <MenuItem value="todos">Todos</MenuItem>
                  <MenuItem value="hoje">Hoje</MenuItem>
                  <MenuItem value="semana">Última semana</MenuItem>
                  <MenuItem value="mes">Último mês</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                size="small"
                label="Data de Importação"
                type="date"
                value={dataImportacaoFilter}
                onChange={(e) => setDataImportacaoFilter(e.target.value)}
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </Grid>
            
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                size="small"
                label="Data Inicial"
                type="date"
                value={dataInicialFilter}
                onChange={(e) => setDataInicialFilter(e.target.value)}
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </Grid>
            
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                size="small"
                label="Data Final"
                type="date"
                value={dataFinalFilter}
                onChange={(e) => setDataFinalFilter(e.target.value)}
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </Grid>
            
            <Grid item xs={12} sm={12} md={12}>
              <Stack direction="row" spacing={1} justifyContent="flex-end">
                <Button
                  variant="outlined"
                  size="small"
                  onClick={handleLimparFiltros}
                >
                  Limpar Filtros
                </Button>
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<RefreshIcon />}
                  onClick={fetchArquivos}
                >
                  Atualizar Lista
                </Button>
              </Stack>
            </Grid>
          </Grid>
          
          {/* Chips para filtros ativos */}
          {(statusFilter !== 'todos' || periodoFilter !== 'todos' || seguradoraFilter !== 'todos' || 
            dataImportacaoFilter || dataInicialFilter || dataFinalFilter || searchTerm) && (
            <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mr: 1, display: 'flex', alignItems: 'center' }}>
                <FilterListIcon fontSize="small" sx={{ mr: 0.5 }} /> 
                Filtros ativos:
              </Typography>
              
              {statusFilter !== 'todos' && (
                <Chip 
                  size="small"
                  label={`Status: ${traduzirStatus(statusFilter)}`}
                  onDelete={() => setStatusFilter('todos')}
                  color={getStatusColor(statusFilter)}
                />
              )}
              
              {periodoFilter !== 'todos' && (
                <Chip 
                  size="small"
                  label={`Período: ${periodoFilter === 'hoje' ? 'Hoje' : periodoFilter === 'semana' ? 'Última semana' : 'Último mês'}`}
                  onDelete={() => setPeriodoFilter('todos')}
                />
              )}
              
              {seguradoraFilter !== 'todos' && (
                <Chip 
                  size="small"
                  label={`Seguradora: ${seguradoraFilter}`}
                  onDelete={() => setSeguradoraFilter('todos')}
                />
              )}
              
              {dataImportacaoFilter && (
                <Chip 
                  size="small"
                  label={`Data Importação: ${formatarDataSimples(dataImportacaoFilter)}`}
                  onDelete={() => setDataImportacaoFilter('')}
                />
              )}
              
              {dataInicialFilter && (
                <Chip 
                  size="small"
                  label={`Data Inicial: ${formatarDataSimples(dataInicialFilter)}`}
                  onDelete={() => setDataInicialFilter('')}
                />
              )}
              
              {dataFinalFilter && (
                <Chip 
                  size="small"
                  label={`Data Final: ${formatarDataSimples(dataFinalFilter)}`}
                  onDelete={() => setDataFinalFilter('')}
                />
              )}
              
              {searchTerm && (
                <Chip 
                  size="small"
                  label={`Busca: ${searchTerm}`}
                  onDelete={() => setSearchTerm('')}
                />
              )}
            </Box>
          )}
        </Paper>
        
        {/* Lista de arquivos */}
        {loading ? (
          <Box sx={{ width: '100%', mb: 4 }}>
            <LinearProgress />
            <Box sx={{ mt: 2 }}>
              {[...Array(5)].map((_, idx) => (
                <Paper key={idx} sx={{ p: 2, mb: 2, opacity: 0.7 }}>
                  <Box sx={{ height: 20, width: '60%', bgcolor: 'rgba(0,0,0,0.1)', borderRadius: 1, mb: 1 }} />
                  <Box sx={{ height: 15, width: '40%', bgcolor: 'rgba(0,0,0,0.1)', borderRadius: 1 }} />
                </Paper>
              ))}
            </Box>
          </Box>
        ) : sortedArquivos.length === 0 ? (
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <InsertChartIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              Nenhum arquivo encontrado
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {searchTerm || statusFilter !== 'todos' || periodoFilter !== 'todos' || seguradoraFilter !== 'todos' || 
               dataImportacaoFilter || dataInicialFilter || dataFinalFilter ?
                'Tente ajustar seus filtros para ver mais resultados' :
                'Comece importando um arquivo para visualizá-lo aqui'}
            </Typography>
            {searchTerm || statusFilter !== 'todos' || periodoFilter !== 'todos' || seguradoraFilter !== 'todos' || 
             dataImportacaoFilter || dataInicialFilter || dataFinalFilter ? (
              <Button
                variant="outlined"
                onClick={handleLimparFiltros}
              >
                Limpar Filtros
              </Button>
            ) : (
              <Button
                variant="contained"
                color="success"
                startIcon={<CloudUploadIcon />}
                onClick={handleOpenUploadDialog}
              >
                Importar Arquivo
              </Button>
            )}
          </Paper>
        ) : (
          <>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="body2" color="text.secondary">
                {totalArquivos === 0
                  ? 'Nenhum arquivo'
                  : `Exibindo ${page * pageSize + 1}–${Math.min((page + 1) * pageSize, totalArquivos)} de ${totalArquivos} ${totalArquivos === 1 ? 'arquivo' : 'arquivos'}`}
              </Typography>
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <Select
                  value={sortOrder}
                  onChange={(e) => setSortOrder(e.target.value)}
                  displayEmpty
                  variant="standard"
                  sx={{ fontSize: '0.875rem' }}
                >
                  <MenuItem value="desc">Mais recentes</MenuItem>
                  <MenuItem value="asc">Mais antigos</MenuItem>
                </Select>
              </FormControl>
            </Box>
            
            {sortedArquivos.map((arquivo) => (
              <Paper key={arquivo.id} sx={{ p: 2, mb: 2, '&:hover': { boxShadow: 3 } }}>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} md={3}>
                    <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 'medium', mb: 0.5 }}>
                      {arquivo.nome || 'Arquivo sem nome'}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                      <Chip
                        size="small"
                        label={arquivo.seguradora}
                        color="primary"
                        variant="outlined"
                      />
                    </Box>
                  </Grid>
                  
                  <Grid item xs={12} md={6}>
                    <Grid container spacing={1}>
                      <Grid item xs={12} sm={6} md={5}>
                        <Typography variant="body2" color="text.secondary">
                          Usuário
                        </Typography>
                        <Typography
                          variant="body2"
                          fontWeight="medium"
                          sx={{
                            whiteSpace: { xs: 'normal', md: 'nowrap' },
                            overflow: 'hidden',
                            textOverflow: 'ellipsis'
                          }}
                        >
                          {arquivo.usuario || 'N/A'}
                        </Typography>
                      </Grid>
                      
                      <Grid item xs={6} sm={3} md={3}>
                        <Typography variant="body2" color="text.secondary">
                          Data Importação
                        </Typography>
                        <Typography variant="body2" fontWeight="medium">
                          {arquivo.data_upload ? formatarData(arquivo.data_upload) : 'N/A'}
                        </Typography>
                      </Grid>
                      
                      <Grid item xs={6} sm={3} md={4}>
                        <Typography variant="body2" color="text.secondary">
                          Período
                        </Typography>
                        <Typography variant="body2" fontWeight="medium">
                          {arquivo.data_inicial ? formatarDataSimples(arquivo.data_inicial) : 'N/A'} a{' '}
                          {arquivo.data_final ? formatarDataSimples(arquivo.data_final) : 'N/A'}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Grid>
                  
                  <Grid item xs={12} md={3} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <Tooltip title="Ações">
                      <IconButton 
                        size="small"
                        onClick={(e) => handleMenuOpen(e, arquivo.id)}
                      >
                        <MoreVertIcon />
                      </IconButton>
                    </Tooltip>
                  </Grid>
                </Grid>
              </Paper>
            ))}

            <TablePagination
              component="div"
              count={totalArquivos}
              page={page}
              onPageChange={(e, newPage) => setPage(newPage)}
              rowsPerPage={pageSize}
              onRowsPerPageChange={(e) => {
                setPageSize(parseInt(e.target.value, 10));
                setPage(0);
              }}
              rowsPerPageOptions={[10, 25, 50, 100]}
              labelRowsPerPage="Arquivos por página:"
              labelDisplayedRows={({ from, to, count }) => `${from}–${to} de ${count}`}
            />

            {/* Menu de ações para o arquivo */}
            <Menu
              anchorEl={menuAnchorEl}
              open={Boolean(menuAnchorEl)}
              onClose={handleMenuClose}
            >
              <MenuItem onClick={() => handleVerRegistrosNaoProcessadosMenu(selectedFileId)}>
                <ListItemIcon>
                  <TableViewIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Ver registros não processados</ListItemText>
              </MenuItem>
              <MenuItem onClick={() => handleExportarRegistrosNaoProcessadosMenu(selectedFileId)}>
                <ListItemIcon>
                  <ErrorOutlineIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Exportar não processados</ListItemText>
              </MenuItem>
              <MenuItem onClick={() => handleOpenDeleteDialog(selectedFileId)}>
                <ListItemIcon>
                  <CloseIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Excluir</ListItemText>
              </MenuItem>
            </Menu>
          </>
        )}
      </Container>
      
      {/* Modal de Upload/Importação */}
      <Dialog 
        open={openUploadModal} 
        onClose={handleCloseUploadDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            {confirmUpload ? 'Confirmar Importação' : 'Importar Novo Arquivo'}
            <IconButton onClick={handleCloseUploadDialog} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        
        <DialogContent>
          {uploadError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {uploadError}
            </Alert>
          )}

          {conflitosTroca.length > 0 && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                Como resolver
              </Typography>
              <Typography variant="body2" sx={{ mb: 2 }}>
                Divida esse arquivo em partes — uma para cada faixa de responsável — e importe cada arquivo separadamente. As datas sugeridas estão na tabela abaixo.
              </Typography>
              <Box sx={{ mb: 1, display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  size="small"
                  variant="outlined"
                  color="warning"
                  onClick={handleDownloadConflitosExcel}
                  startIcon={<TableViewIcon />}
                >
                  Baixar em Excel
                </Button>
              </Box>
              <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 320 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>Corretora / Seguradora</strong></TableCell>
                      <TableCell><strong>Responsável anterior</strong></TableCell>
                      <TableCell><strong>Novo responsável</strong></TableCell>
                      <TableCell><strong>Data da troca</strong></TableCell>
                      <TableCell><strong>Sugestão de divisão</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {conflitosTroca.map((c, i) => (
                      <TableRow key={`${c.empresa}-${c.seguradora}-${i}`}>
                        <TableCell>
                          <div>{c.empresa}</div>
                          <Typography variant="caption" color="text.secondary">{c.seguradora}</Typography>
                        </TableCell>
                        <TableCell>{c.responsavel_antigo}</TableCell>
                        <TableCell>{c.responsavel_novo}</TableCell>
                        <TableCell>{c.data_troca}</TableCell>
                        <TableCell>
                          <Typography variant="caption" component="div">Arquivo 1: {c.sugestao?.arquivo_1}</Typography>
                          <Typography variant="caption" component="div">Arquivo 2: {c.sugestao?.arquivo_2}</Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Alert>
          )}

          {!confirmUpload ? (
            <>
              <DialogContentText sx={{ mb: 3 }}>
                Selecione um arquivo Excel para importar e informe o período de referência dos dados.
              </DialogContentText>
              
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <FormControl fullWidth variant="outlined">
                    <InputLabel shrink htmlFor="arquivo-upload">
                      Arquivo
                    </InputLabel>
                    <Box 
                      sx={{ 
                        mt: 2, 
                        border: '1px dashed',
                        borderColor: 'divider',
                        borderRadius: 1,
                        p: 3,
                        textAlign: 'center'
                      }}
                    >
                      <input
                        accept=".xlsx,.xls,.csv"
                        style={{ display: 'none' }}
                        id="arquivo-upload"
                        type="file"
                        onChange={handleFileChange}
                      />
                      <label htmlFor="arquivo-upload">
                        <Button
                          variant="outlined"
                          component="span"
                          startIcon={<CloudUploadIcon />}
                        >
                          Selecionar Arquivo
                        </Button>
                      </label>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        {file ? `Arquivo selecionado: ${file.name}` : 'Formatos suportados: Excel (.xlsx, .xls) e CSV'}
                      </Typography>
                    </Box>
                  </FormControl>
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Data Inicial"
                    type="date"
                    value={dataInicial}
                    onChange={(e) => setDataInicial(e.target.value)}
                    InputLabelProps={{
                      shrink: true,
                    }}
                    disabled={seguradoraSelecionada === 'DEMAIS_SEGURADORAS'}
                    helperText={seguradoraSelecionada === 'DEMAIS_SEGURADORAS' ? 'Não aplicável para Demais Seguradoras' : ''}
                  />
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Data Final"
                    type="date"
                    value={dataFinal}
                    onChange={(e) => setDataFinal(e.target.value)}
                    InputLabelProps={{
                      shrink: true,
                    }}
                    disabled={seguradoraSelecionada === 'DEMAIS_SEGURADORAS'}
                    helperText={seguradoraSelecionada === 'DEMAIS_SEGURADORAS' ? 'Não aplicável para Demais Seguradoras' : ''}
                  />
                </Grid>
                
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <InputLabel>Seguradora</InputLabel>
                    <Select
                      value={seguradoraSelecionada}
                      label="Seguradora"
                      onChange={(e) => setSeguradoraSelecionada(e.target.value)}
                    >
                      <MenuItem value="">Selecione uma seguradora</MenuItem>
                      {SEGURADORAS.map((seg) => (
                        <MenuItem key={seg} value={seg}>
                          {seg === 'DEMAIS_SEGURADORAS' ? 'Demais Seguradoras (Padrão)' : seg.charAt(0) + seg.slice(1).toLowerCase()}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </>
          ) : (
            <>
              <DialogContentText sx={{ mb: 3 }}>
                O arquivo foi processado com sucesso. Por favor, confira a prévia abaixo e confirme a importação.
              </DialogContentText>
              
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Informações do Arquivo:
                </Typography>
                <Typography variant="body2">
                  <strong>Nome:</strong> {dadosTemporarios?.nome_arquivo || 'N/A'}
                </Typography>
                
                {/* Mostrar período apenas para seguradoras que não sejam DEMAIS_SEGURADORAS */}
                {dadosTemporarios?.seguradora !== 'DEMAIS_SEGURADORAS' && (
                <Typography variant="body2">
                  <strong>Período:</strong> {dadosTemporarios?.data_inicial || 'N/A'} a {dadosTemporarios?.data_final || 'N/A'}
                </Typography>
                )}
                
                <Typography variant="body2">
                  <strong>Tipo:</strong> {dadosTemporarios?.seguradora === 'DEMAIS_SEGURADORAS' ? 'Demais Seguradoras (Múltiplas)' : dadosTemporarios?.seguradora || 'N/A'}
                </Typography>
                
                {/* Para DEMAIS_SEGURADORAS, mostrar estatísticas detalhadas */}
                {(dadosTemporarios?.seguradora === 'DEMAIS_SEGURADORAS' || seguradoraSelecionada === 'DEMAIS_SEGURADORAS') && (dadosTemporarios?.stats_por_seguradora || dadosTemporarios?.total_premio_geral) ? (
                  <>
                    <Box sx={{ mt: 2, p: 2, bgcolor: 'primary.50', borderRadius: 1 }}>
                      <Typography variant="subtitle2" sx={{ color: 'primary.main', mb: 1 }}>
                        📊 TOTAIS GERAIS
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                        • Total de registros: {(dadosTemporarios?.total_registros || totalRegistros || 0).toLocaleString('pt-BR')}
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'success.main' }}>
                        • Valor total: R$ {(dadosTemporarios?.total_premio_geral || (() => {
                          // Fallback: calcular soma dos preview data se não tiver o total do backend
                          if (previewData && Array.isArray(previewData)) {
                            return previewData.reduce((sum, item) => sum + (parseFloat(item.premio_liquido) || 0), 0);
                          }
                          return 0;
                        })()).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                        • Seguradoras: {dadosTemporarios?.total_seguradoras || (() => {
                          // Fallback: contar seguradoras únicas nos preview data
                          if (previewData && Array.isArray(previewData)) {
                            return new Set(previewData.map(item => item.seguradora)).size;
                          }
                          return 0;
                        })()}
                      </Typography>
                    </Box>
                    
                    <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                      <Typography variant="subtitle2" sx={{ mb: 1 }}>
                        📋 DETALHES POR SEGURADORA
                      </Typography>
                      {(() => {
                        // Usar stats do backend se disponível, senão calcular dos preview data
                        const stats = dadosTemporarios?.stats_por_seguradora || {};
                        
                        // Se não tiver stats do backend, calcular dos preview data
                        if (Object.keys(stats).length === 0 && previewData && Array.isArray(previewData)) {
                          const calculatedStats = {};
                          previewData.forEach(item => {
                            const seg = item.seguradora;
                            if (!calculatedStats[seg]) {
                              calculatedStats[seg] = { registros: 0, total_premio: 0 };
                            }
                            calculatedStats[seg].registros += 1;
                            calculatedStats[seg].total_premio += parseFloat(item.premio_liquido) || 0;
                          });
                          
                          const totalGeral = Object.values(calculatedStats).reduce((sum, s) => sum + s.total_premio, 0);
                          
                          return Object.entries(calculatedStats).map(([seguradora, statsData]) => {
                            const percentual = totalGeral > 0 ? (statsData.total_premio / totalGeral * 100) : 0;
                            return (
                              <Typography key={seguradora} variant="body2" sx={{ mb: 0.5 }}>
                                • <strong>{seguradora}:</strong> {statsData.registros} registros 
                                (R$ {statsData.total_premio.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} - {percentual.toFixed(1)}%)
                              </Typography>
                            );
                          });
                        }
                        
                        // Usar stats do backend
                        const totalGeral = dadosTemporarios?.total_premio_geral || Object.values(stats).reduce((sum, s) => sum + s.total_premio, 0);
                        
                        return Object.entries(stats).map(([seguradora, statsData]) => {
                          const percentual = totalGeral > 0 ? (statsData.total_premio / totalGeral * 100) : 0;
                          return (
                            <Typography key={seguradora} variant="body2" sx={{ mb: 0.5 }}>
                              • <strong>{seguradora}:</strong> {statsData.registros} registros 
                              (R$ {statsData.total_premio.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} - {percentual.toFixed(1)}%)
                            </Typography>
                          );
                        });
                      })()}
                    </Box>
                  </>
                ) : (
                  /* Para outras seguradoras, usar o cálculo tradicional */
                  <>
                <Typography variant="body2" sx={{ mt: 1, color: 'primary.main', fontWeight: 'bold' }}>
                  <strong>Total de Registros:</strong> {totalRegistros ? totalRegistros.toLocaleString('pt-BR') : '0'}
                </Typography>
                <Typography variant="body2" sx={{ mt: 0.5, color: 'success.main', fontWeight: 'bold' }}>
                  <strong>Total de Prêmios (pré-visualização):</strong> {(() => {
                    const soma = calcularTotalPremios();
                    return soma.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
                  })()}
                </Typography>
                  </>
                )}
              </Box>
              
              {previewData && previewData.length > 0 ? (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Prévia dos Dados:
                  </Typography>
                  {console.log("Renderizando prévia com dados:", previewData)}
                  <TableContainer component={Paper} variant="outlined" sx={{ overflowX: 'auto', maxHeight: 400 }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow sx={{ bgcolor: 'grey.50' }}>
                          {previewData && previewData.length > 0 && Object.keys(previewData[0] || {}).map((key) => (
                            <TableCell key={key} sx={{ fontWeight: 'bold' }}>{key}</TableCell>
                          ))}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {previewData.map((row, index) => (
                          <TableRow key={index} sx={{ '&:nth-of-type(odd)': { backgroundColor: 'rgba(0, 0, 0, 0.03)' } }}>
                            {previewData && previewData.length > 0 && Object.keys(previewData[0] || {}).map((key, i) => {
                              const value = row[key];
                              
                              // Formatação por tipo de valor
                              if (value === null || value === undefined || value === "null" || value === "undefined" || value === "NaN" || (typeof value === 'number' && isNaN(value))) {
                                return <TableCell key={i}>-</TableCell>;
                              } else if (typeof value === 'number') {
                                // Verificar se o valor parece ser monetário (coluna de prêmio, valor, etc.)
                                const isMonetaryColumn = key.toLowerCase().includes('premio') || 
                                                      key.toLowerCase().includes('prêmio') || 
                                                      key.toLowerCase().includes('valor') ||
                                                      key.toLowerCase().includes('total');
                                                      
                                // Para colunas monetárias, sempre mostrar com 2 casas decimais
                                if (isMonetaryColumn) {
                                  // Importante: o toLocaleString precisa da configuração explícita para garantir 2 casas decimais mesmo em valores "redondos"
                                  return <TableCell key={i}>{value.toLocaleString('pt-BR', {
                                    minimumFractionDigits: 2, 
                                    maximumFractionDigits: 2,
                                    useGrouping: true
                                  })}</TableCell>;
                                } else {
                                  return <TableCell key={i}>{value.toLocaleString('pt-BR')}</TableCell>;
                                }
                              } else if (typeof value === 'string') {
                                // Para strings que representam valores monetários
                                const isMonetaryColumn = key.toLowerCase().includes('premio') || 
                                                       key.toLowerCase().includes('prêmio') || 
                                                       key.toLowerCase().includes('valor') ||
                                                       key.toLowerCase().includes('total');
                                
                                if (isMonetaryColumn) {
                                  // Tentar converter para número primeiro
                                  let numValue;
                                  try {
                                    // Remover pontos de milhar e substituir vírgula por ponto decimal
                                    const valueProcessed = value.replace(/\./g, '').replace(',', '.');
                                    numValue = parseFloat(valueProcessed);
                                    if (!isNaN(numValue)) {
                                      return <TableCell key={i}>{numValue.toLocaleString('pt-BR', {
                                        minimumFractionDigits: 2, 
                                        maximumFractionDigits: 2,
                                        useGrouping: true
                                      })}</TableCell>;
                                    }
                                  } catch(e) {
                                    // Se não conseguir converter, cai para o tratamento padrão de string
                                    console.log("Erro ao converter string para número:", e);
                                  }
                                }
                                
                                // Decodificar sequências Unicode como \u00e7\u00e3o
                                let displayValue = String(value);
                                try {
                                  // Tenta decodificar sequências Unicode se existirem
                                  // Procura por padrões \\u seguido de 4 caracteres hexadecimais
                                  if (displayValue.includes('\\u')) {
                                    // Substituir sequências com escape duplo \\u00e7
                                    displayValue = displayValue.replace(/\\u([0-9a-f]{4})/gi, (match, hex) => {
                                      return String.fromCharCode(parseInt(hex, 16));
                                    });
                                  }
                                  
                                  // Para valores específicos, substituir diretamente
                                  if (displayValue === 'Nova Cota\\u00e7\\u00e3o') {
                                    displayValue = 'Nova Cotação';
                                  }
                                  
                                  // Converte as entidades HTML se houver
                                  const parser = new DOMParser();
                                  const dom = parser.parseFromString(
                                    '<!doctype html><body>' + displayValue, 'text/html');
                                  displayValue = dom.body.textContent || displayValue;
                                } catch (e) {
                                  console.warn('Erro ao decodificar texto:', e);
                                }
                                return <TableCell key={i}>{displayValue}</TableCell>;
                              } else if (typeof value === 'boolean') {
                                return <TableCell key={i}>{value ? 'Sim' : 'Não'}</TableCell>;
                              } else {
                                return <TableCell key={i}>{String(value)}</TableCell>;
                              }
                            })}
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                    * Mostrando {previewData.length} linha(s) de prévia do arquivo.
                  </Typography>
                </Box>
              ) : (
                <Alert severity="info" sx={{ mb: 2 }}>
                  Nenhum dado de prévia disponível para este arquivo.
                </Alert>
              )}
            </>
          )}
        </DialogContent>
        
        <DialogActions sx={{ px: 3, pb: 3 }}>
          {!confirmUpload ? (
            <>
              <Button 
                onClick={handleCloseUploadDialog}
                disabled={uploadLoading}
              >
                Cancelar
              </Button>
              <Button 
                variant="contained" 
                onClick={handleProcessarArquivo}
                disabled={
                  !file || 
                  !seguradoraSelecionada || 
                  (seguradoraSelecionada !== 'DEMAIS_SEGURADORAS' && (!dataInicial || !dataFinal)) || 
                  uploadLoading
                }
                startIcon={uploadLoading ? <CircularProgress size={20} /> : <CloudUploadIcon />}
              >
                {uploadLoading ? 'Processando...' : 'Processar Arquivo'}
              </Button>
            </>
          ) : (
            <>
              <Button 
                onClick={handleCancelarImportacao}
                disabled={uploadLoading}
              >
                Cancelar
              </Button>
              <Button 
                variant="contained" 
                color="primary"
                onClick={handleConfirmarImportacao}
                disabled={uploadLoading}
                startIcon={uploadLoading ? <CircularProgress size={20} /> : null}
              >
                {uploadLoading ? 'Confirmando...' : 'Confirmar Importação'}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      {/* Diálogo de confirmação de exclusão */}
      <Dialog
        open={openDeleteDialog}
        onClose={handleCloseDeleteDialog}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        maxWidth="xs"
      >
        <DialogTitle id="alert-dialog-title" sx={{ pb: 1 }}>
          <Box display="flex" alignItems="center">
            <CloseIcon color="error" sx={{ mr: 1 }} />
            Confirmar Exclusão
          </Box>
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Tem certeza que deseja excluir esta importação?
          </DialogContentText>
          <Typography variant="body2" color="error.main" sx={{ mt: 1, fontWeight: 'medium' }}>
            Esta ação não pode ser desfeita.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button 
            onClick={handleCloseDeleteDialog} 
            variant="outlined"
            disabled={loading}
          >
            Cancelar
          </Button>
          <Button 
            onClick={handleExcluirImportacao} 
            color="error" 
            variant="contained"
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} color="inherit" /> : null}
          >
            {loading ? 'Excluindo...' : 'Confirmar Exclusão'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Diálogo de registros não processados */}
      <Dialog
        open={openNaoProcessadosDialog}
        onClose={() => setOpenNaoProcessadosDialog(false)}
        maxWidth={corretorasSemComercial.length > 0 ? 'md' : 'sm'}
        fullWidth
      >
        <DialogTitle>
          Atenção - Pendências da Importação
          <IconButton
            aria-label="close"
            onClick={() => setOpenNaoProcessadosDialog(false)}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {registrosNaoProcessados > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }} gutterBottom>
                Registros não processados
              </Typography>
              <Typography variant="body1" gutterBottom>
                A importação foi concluída, mas {registrosNaoProcessados} registros não puderam ser processados 
                por falta de cadastro no sistema.
              </Typography>
              <Typography variant="body1">
                Você pode visualizar esses registros ou exportá-los para Excel.
              </Typography>
            </Box>
          )}
          {corretorasSemComercial.length > 0 && (
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }} gutterBottom>
                Corretoras sem comercial no período
              </Typography>
              <Alert severity="warning" sx={{ mb: 2 }}>
                Os valores destas corretoras foram importados, mas aparecem como "SEM RESPONSAVEL" no BI e no
                Relatório de Resultados. Cadastre o comercial delas para o período da importação e o BI será
                atualizado automaticamente.
              </Alert>
              <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 320 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>Corretora</TableCell>
                      <TableCell>Seguradora</TableCell>
                      <TableCell align="right">Registros</TableCell>
                      <TableCell align="right">Valor</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {corretorasSemComercial.map((c) => (
                      <TableRow key={`${c.id_corretora}-${c.seguradora}`}>
                        <TableCell>{c.corretora}</TableCell>
                        <TableCell>{c.seguradora}</TableCell>
                        <TableCell align="right">{c.registros}</TableCell>
                        <TableCell align="right">
                          {Number(c.valor || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button 
            variant="outlined" 
            onClick={() => setOpenNaoProcessadosDialog(false)}
          >
            Fechar
          </Button>
          {registrosNaoProcessados > 0 && (
            <>
              <Button 
                variant="contained" 
                color="primary"
                onClick={handleExportarRegistrosNaoProcessados}
              >
                Exportar para Excel
              </Button>
              <Button 
                variant="contained" 
                color="secondary"
                onClick={handleVerRegistrosNaoProcessados}
              >
                Visualizar Registros
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
    </Layout>
  );
};

export default Dashboard;
